/**
 * Read-only list of `menu_items` for a `MenuPlan` detail page,
 * with an inline Amend button per row (sub-PR #3 of issue #156).
 *
 * The detail endpoint (`GET /menu-planning/plans/:id`) returns
 * `MenuPlanWithItems` — envelope + items array. ResourcePage's
 * generic detail signature narrows the row to `MenuPlan`, so at
 * runtime the augmentation is present but TS does not see it.
 * The cast at the entry point is the boundary; downstream code
 * is strongly typed.
 *
 * Amend button visibility (mirror the M2.5 INV-M2-9 contract):
 *   - plan.status === 'published'
 *   - operator has `menu_plans:amend`
 *   - item.delivery_date in {today, tomorrow}
 * Showing the button when it would unconditionally 409 would be
 * the EQD §3 silent-fallback antipattern in reverse — the user
 * doesn't get a silent error, they get a guaranteed click → toast
 * → frustration cycle. Filter at the UI.
 *
 * Out of scope this PR: in-place item edit, adding rows. Those
 * are sub-PR #5 (week page calendar) territory.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, Badge, Button } from '@resource-kit/ui/react';
import { useToday } from '@resource-kit/controllers/react';
import { useBetterAuth } from '@hooks/useAuth';
import { PERMISSIONS } from '@/permissions';
import { lookupEnumLabelOrWarn } from '@/lib/enum-label';
import { menuItemStatusOptions } from '../schema';
import {
  type MenuPlan,
  type MenuItem,
  type MenuItemStatus,
  type MenuPlanWithItems,
} from '@/services';
import { AmendMenuItemDialog, isInAmendWindow } from './AmendMenuItemDialog';

interface Props {
  plan: MenuPlan;
}

const STATUS_BADGE_VARIANT: Record<
  MenuItemStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  planned: 'secondary',
  amended: 'default',
  requires_review: 'destructive',
};

/** Extract dish display names from the snapshot JSON.
 *
 *  Trusts the M2 service's zod boundary (INV-M2-7 fail-loud) — the
 *  snapshot is structurally a JSON array of `{dish_id, name, photos}`.
 *  We deliberately do NOT wrap `JSON.parse` in try/catch: a corrupt
 *  snapshot is a structural invariant violation that should bubble
 *  to the route's ErrorBoundary, not silently render "—" and rot.
 *  EQD §3 (no silent fallback) + EQD §7 (prefer deletion over
 *  defense layers).
 *
 *  `name` can be missing on legacy snapshots written before the
 *  M2.4 projection landed; we narrow per-entry and fall back to
 *  the i18n-keyed `noDishes` string only when there genuinely are
 *  no names — empty array, not error suppression. */
function renderDishNames(item: MenuItem, fallback: string): string {
  const parsed = JSON.parse(item.dishes) as Array<{ name?: string }>;
  const names = parsed
    .map((d) => (typeof d?.name === 'string' ? d.name : null))
    .filter((n): n is string => Boolean(n));
  return names.length > 0 ? names.join(' · ') : fallback;
}

/** Render the backend-emitted `requires_review_reason` string through i18n.
 *
 *  The backend menu-events subscriber emits messages shaped like
 *  `<event-token>: <english debug detail>` — see
 *  `backend/src/modules/menu-planning/events/subscribers.ts`. Examples
 *  the M2.4 wiring currently produces:
 *
 *    `catalog.dish_archived: dish d-123 was archived and is no longer available`
 *    `catalog.ingredient_archived: ingredient ing-456 was archived ...`
 *
 *  Strategy: split on the first `:`, look up the prefix as a locale
 *  key (`menu_plan.reviewReason.<token>`), and append the raw detail
 *  in parentheses for operators who need the specific entity id.
 *  Falls back to the full raw string when the prefix doesn't match a
 *  known reason — surfaces the drift instead of rendering blank
 *  (CLAUDE.md i18n hard rule against raw English in JSX). */
function renderReviewReason(
  raw: string,
  t: (key: string, opts?: { defaultValue?: string }) => string,
): string {
  const colonIndex = raw.indexOf(':');
  if (colonIndex === -1) {
    return t(`menu_plan.reviewReason.${raw}`, { defaultValue: raw });
  }
  const token = raw.slice(0, colonIndex).trim();
  const detail = raw.slice(colonIndex + 1).trim();
  const localised = t(`menu_plan.reviewReason.${token}`, {
    defaultValue: raw,
  });
  // When the token resolved to a localised string the lookup returned
  // something OTHER than the defaultValue raw. If localisation succeeded
  // we append the entity-specific detail; if it fell back to the raw we
  // already have the detail in the string, no duplication.
  return localised === raw ? raw : `${localised} (${detail})`;
}

export function MenuItemsCard({ plan }: Props) {
  const { t } = useTranslation();
  const { user } = useBetterAuth();
  const today = useToday();
  // ResourcePage's detail loader hands us the augmented row; the cast
  // is the boundary between the framework's generic T and our domain
  // type. Downstream code reads `items` directly.
  const items = (plan as MenuPlanWithItems).items ?? [];
  const [amendTarget, setAmendTarget] = useState<MenuItem | null>(null);

  const canAmend =
    plan.status === 'published' &&
    // Amend re-gated onto `catalog:write` — `menu_plans:amend` folded
    // into the unified `catalog:*` module in the backend redesign (#943).
    (user?.permissions?.includes(PERMISSIONS.CATALOG_WRITE) ?? false);
  // Pre-compute Amend column visibility once: if no item is in-window
  // AND the operator has no amend grant, drop the column entirely.
  // Spurious empty columns are noise — same EQD §7 deletion principle
  // the LifecycleActionsToolbar uses to return null when there's no
  // action to show.
  const showAmendColumn = canAmend && items.some((it) => isInAmendWindow(it.delivery_date, today));

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('menu_plan.items.title', { count: items.length })}</CardTitle>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('menu_plan.items.empty')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-muted-foreground">
                <tr className="border-b border-border">
                  <th className="py-2 pr-4 text-left font-medium">
                    {t('menu_plan.items.col.deliveryDate')}
                  </th>
                  <th className="py-2 pr-4 text-left font-medium">
                    {t('menu_plan.items.col.combo')}
                  </th>
                  <th className="py-2 pr-4 text-left font-medium">
                    {t('menu_plan.items.col.dishes')}
                  </th>
                  <th className="py-2 pr-4 text-left font-medium">
                    {t('menu_plan.items.col.status')}
                  </th>
                  {showAmendColumn && (
                    <th className="py-2 pr-4 text-left font-medium">
                      {t('menu_plan.items.col.actions')}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const itemInWindow = canAmend && isInAmendWindow(item.delivery_date, today);
                  return (
                    <tr key={item.id} className="border-b border-border last:border-0">
                      <td className="py-2 pr-4 font-mono text-xs">{item.delivery_date}</td>
                      <td className="py-2 pr-4 font-mono text-xs">{item.combo_id}</td>
                      <td className="py-2 pr-4">
                        {renderDishNames(item, t('menu_plan.items.noDishes'))}
                      </td>
                      <td className="py-2 pr-4">
                        <Badge variant={STATUS_BADGE_VARIANT[item.status]}>
                          {/* #345 PR #353: pre-fix unknown
                              `item.status` would render the raw
                              i18n key literally
                              (`menu_plan.enum.itemStatus.xxx`).
                              Route through the SOT options table
                              for fail-loud drift. */}
                          {lookupEnumLabelOrWarn(
                            item.status,
                            menuItemStatusOptions,
                            'menu item status',
                          )}
                        </Badge>
                        {item.requires_review_reason && (
                          <p className="mt-1 text-xs text-destructive">
                            {renderReviewReason(item.requires_review_reason, t)}
                          </p>
                        )}
                      </td>
                      {showAmendColumn && (
                        <td className="py-2 pr-4">
                          {itemInWindow ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setAmendTarget(item)}
                              data-testid="menu-plan-amend"
                            >
                              <Pencil className="mr-1 h-3 w-3" />
                              {t('menu_plan.amend.openButton')}
                            </Button>
                          ) : null}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
      {amendTarget && (
        <AmendMenuItemDialog
          open={!!amendTarget}
          onClose={() => setAmendTarget(null)}
          planId={plan.id}
          item={amendTarget}
        />
      )}
    </Card>
  );
}
